import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sparkles, Upload, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AITaskCreator = ({ isOpen, onClose, projectId, getAuthHeader, onTasksCreated }) => {
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  };

  const handlePaste = async (e) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));
    
    if (imageItems.length > 0) {
      e.preventDefault();
      const files = await Promise.all(
        imageItems.map(item => item.getAsFile())
      );
      await handleFiles(files);
    }
  };

  const handleFiles = async (files) => {
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    
    for (const file of validFiles) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setImages(prev => [...prev, {
          id: Math.random().toString(36),
          name: file.name,
          base64: base64,
          preview: reader.result
        }]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInput = async (e) => {
    const files = Array.from(e.target.files);
    await handleFiles(files);
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const handleGenerate = async () => {
    if (!text.trim() && images.length === 0) {
      toast.error('Bitte geben Sie Text ein oder fügen Sie Bilder hinzu');
      return;
    }

    setIsGenerating(true);
    try {
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('text', text);
      
      if (images.length > 0) {
        const imageData = images.map(img => ({ base64: img.base64 }));
        formData.append('images', JSON.stringify(imageData));
      }

      const response = await axios.post(`${API}/tasks/ai-create`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message);
      setText('');
      setImages([]);
      onClose();
      onTasksCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Task-Generierung');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto" aria-describedby="dialog-ai-task-description">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <span>AI Task Creator</span>
          </DialogTitle>
          <DialogDescription id="dialog-ai-task-description">
            Fügen Sie Text oder Screenshots ein. Die KI extrahiert automatisch Tasks daraus.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Text Input */}
          <div>
            <Label htmlFor="ai-text-input">Text oder Anforderungen einfügen</Label>
            <Textarea
              id="ai-text-input"
              data-testid="ai-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={handlePaste}
              placeholder="Fügen Sie hier Text ein oder drücken Sie Strg+V für Screenshots...

Beispiel:
- Implementierung des Login-Systems bis Freitag
- Datenbank-Schema für Benutzerverwaltung erstellen
- Frontend-Design für Dashboard überarbeiten"
              className="mt-2 min-h-[200px]"
            />
            <p className="text-xs text-gray-500 mt-2">
              💡 Tipp: Strg+V funktioniert auch für Screenshots aus der Zwischenablage
            </p>
          </div>

          {/* Image Upload Area */}
          <div>
            <Label>Screenshots / Bilder</Label>
            <div
              className={`mt-2 border-2 border-dashed rounded-lg p-8 text-center ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-sm text-gray-600 mb-2">
                Bilder hier ablegen oder klicken zum Hochladen
              </p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileInput}
                className="hidden"
                id="file-upload"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('file-upload').click()}
                className="mt-2"
              >
                Dateien auswählen
              </Button>
            </div>
          </div>

          {/* Image Previews */}
          {images.length > 0 && (
            <div>
              <Label>Hochgeladene Bilder ({images.length})</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                {images.map(img => (
                  <div key={img.id} className="relative group">
                    <img
                      src={img.preview}
                      alt={img.name}
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <button
                      onClick={() => removeImage(img.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-xs text-gray-600 mt-1 truncate">{img.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generate Button */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || (!text.trim() && images.length === 0)}
              data-testid="generate-tasks-button"
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white flex items-center space-x-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Generiere Tasks...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Tasks generieren</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AITaskCreator;
